import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

function formatTime(seconds) {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function splitText(text) {
  return (text || '').trim().split(/\s+/).filter(Boolean);
}

function TranscriptionSegment({
  segment,
  index,
  speakerColor,
  activeWordIndex,
  onWordClick,
  onWordAction,
  onSaveText,
}) {
  const speakerLabel = segment.speaker || `SPEAKER_${index}`;
  const words = segment.words;
  const hasWords = Array.isArray(words) && words.length > 0;
  const wordText = hasWords ? words.map((word) => word.word).join(' ') : '';
  const displayText = segment.text || wordText;
  const displayTokens = splitText(displayText);
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(displayText);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const inputRef = React.useRef(null);
  const skipBlurRef = React.useRef(false);
  const committingRef = React.useRef(false);

  React.useEffect(() => {
    if (!editing) setDraft(displayText);
  }, [displayText, editing]);

  React.useEffect(() => {
    if (!editing) return undefined;
    const t = setTimeout(() => inputRef.current?.focus?.(), 0);
    return () => clearTimeout(t);
  }, [editing]);

  const startEdit = () => {
    if (saving) return;
    setDraft(displayText);
    setError('');
    setEditing(true);
  };

  const jumpToSegmentStart = () => {
    if (editing || typeof segment.start !== 'number') return;
    onWordClick?.(segment.start);
  };

  const cancelEdit = () => {
    skipBlurRef.current = true;
    setDraft(displayText);
    setError('');
    setEditing(false);
  };

  const commitEdit = async () => {
    if (!editing || committingRef.current) return;
    const next = draft.trim();
    if (next === displayText.trim()) {
      setEditing(false);
      setError('');
      return;
    }
    committingRef.current = true;
    setSaving(true);
    setError('');
    try {
      await onSaveText?.(index, next);
      setEditing(false);
    } catch (e) {
      setError(e.message || 'Speichern fehlgeschlagen');
    } finally {
      committingRef.current = false;
      setSaving(false);
    }
  };

  const handleBlur = () => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    commitEdit();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
      return;
    }
    if (
      event.key === 'Enter' &&
      (!event.shiftKey || event.metaKey || event.ctrlKey)
    ) {
      event.preventDefault();
      commitEdit();
    }
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.5, minHeight: 24 }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: speakerColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: speakerColor,
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          {speakerLabel}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          onClick={jumpToSegmentStart}
          sx={{
            cursor: 'pointer',
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
            '&:hover': { color: 'primary.main' },
          }}
          title="Zum Segment springen"
        >
          {formatTime(segment.start)} – {formatTime(segment.end)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton
          size="small"
          onClick={startEdit}
          disabled={saving}
          aria-label="Segment bearbeiten"
          title="Segment bearbeiten"
          sx={{ ml: 'auto' }}
        >
          <EditOutlinedIcon fontSize="inherit" />
        </IconButton>
      </Stack>

      {editing ? (
        <Box>
          <TextField
            inputRef={inputRef}
            fullWidth
            multiline
            minRows={2}
            value={draft}
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            size="small"
          />
          {saving && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Speichern …
              </Typography>
            </Stack>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}
        </Box>
      ) : (
        <Typography
          variant="body1"
          component="div"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.65,
            color: 'text.primary',
          }}
        >
          {hasWords && displayTokens.length > 0
            ? displayTokens.map((token, idx) => {
                const word = words[idx];
                const canJump = typeof word?.start === 'number';
                const isActive = idx === activeWordIndex;
                return (
                  <span
                    key={idx}
                    title={
                      canJump
                        ? `${formatTime(word.start)} – ${formatTime(word.end)}`
                        : undefined
                    }
                    onClick={(event) => {
                      if (!canJump) return;
                      // Prefer the popover (action menu) when wired; fall back
                      // to direct audio jump for callers that don't use it.
                      if (onWordAction) {
                        onWordAction({
                          segmentIndex: index,
                          wordIndex: idx,
                          word,
                          anchorEl: event.currentTarget,
                          totalWords: words.length,
                          wordStart: word.start,
                        });
                      } else {
                        onWordClick?.(word.start);
                      }
                    }}
                    className={isActive ? 'tx-word tx-word-active' : 'tx-word'}
                  >
                    {token}
                    {idx < displayTokens.length - 1 ? ' ' : ''}
                  </span>
                );
              })
            : segment.text}
        </Typography>
      )}
    </Paper>
  );
}

export default React.memo(TranscriptionSegment);
